import { BellOff, BellRing, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/ui/lib/utils";

export type MapMode = "custom" | "alert-preview" | "alert-review-v2";

const MODES: { value: MapMode; labelKey: "custom" | "alertPreview" | "alertReviewV2" }[] = [
  { value: "custom", labelKey: "custom" },
  { value: "alert-preview", labelKey: "alertPreview" },
  { value: "alert-review-v2", labelKey: "alertReviewV2" },
];

interface MapModeBarProps {
  mode: MapMode;
  onModeChange: (mode: MapMode) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Whether an arriving alert may take over the map, timeline and video. */
  followNewAlerts: boolean;
  onToggleFollowNewAlerts: () => void;
}

/**
 * Map-top mode selector. Collapsible via a chevron. "Custom" = free use;
 * "Alert · review" turns on the alert automation (30s polling + new-alerts
 * badge + click-to-focus). "Alert review V2" runs the same automation but
 * swaps the floating card for the on-map review overlay.
 *
 * Also carries the live-alert follow switch, in the alert modes that have
 * something to follow. It stays out of the collapsing section on purpose: an
 * operator who has stopped following needs to SEE that they have, or they will
 * read a quiet map as a quiet site.
 */
const MapModeBar = ({
  mode,
  onModeChange,
  collapsed,
  onToggleCollapsed,
  followNewAlerts,
  onToggleFollowNewAlerts,
}: MapModeBarProps) => {
  const { t } = useTranslation("map");
  const current = MODES.find((m) => m.value === mode);
  const alertMode = mode !== "custom";

  return (
    <div className="absolute left-1/2 top-3 z-20 -translate-x-1/2">
      <div className="flex items-center gap-1 rounded-full border border-border bg-background/95 px-2 py-1 shadow-md backdrop-blur transition-all duration-200">
        <span className="pl-1 pr-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t("modeBar.label")}
        </span>

        {/* Collapsed: compact current-mode label (animated width). */}
        <span
          className={cn(
            "overflow-hidden whitespace-nowrap text-xs font-semibold transition-all duration-200",
            collapsed ? "max-w-[180px] px-1 opacity-100" : "max-w-0 opacity-0",
          )}
        >
          {current ? t(`modeBar.modes.${current.labelKey}`) : null}
        </span>

        {/* Expanded: segmented switch (animated width). */}
        <div
          className={cn(
            "flex items-center gap-0.5 overflow-hidden rounded-full bg-muted/60 transition-all duration-200",
            collapsed ? "max-w-0 p-0 opacity-0" : "max-w-[420px] p-0.5 opacity-100",
          )}
        >
          {MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => onModeChange(m.value)}
              className={cn(
                "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium transition-all duration-200",
                mode === m.value
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t(`modeBar.modes.${m.labelKey}`)}
            </button>
          ))}
        </div>

        {alertMode && (
          <>
            <span className="mx-0.5 h-4 w-px shrink-0 bg-border" aria-hidden />
            <button
              type="button"
              role="switch"
              aria-checked={followNewAlerts}
              onClick={onToggleFollowNewAlerts}
              title={
                followNewAlerts
                  ? t("modeBar.followOnTitle")
                  : t("modeBar.followOffTitle")
              }
              aria-label={
                followNewAlerts ? t("modeBar.stopFollowing") : t("modeBar.startFollowing")
              }
              className={cn(
                "flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium transition-colors",
                followNewAlerts
                  ? "text-muted-foreground hover:bg-muted hover:text-foreground"
                  : "bg-amber-500/15 text-amber-600 hover:bg-amber-500/25 dark:text-amber-400",
              )}
            >
              {followNewAlerts ? (
                <BellRing className="h-3.5 w-3.5" />
              ) : (
                <BellOff className="h-3.5 w-3.5" />
              )}
              <span className="whitespace-nowrap">{t("modeBar.liveAlerts")}</span>
            </button>
          </>
        )}

        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? t("modeBar.expand") : t("modeBar.collapse")}
          className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ChevronUp className={cn("h-4 w-4 transition-transform duration-200", collapsed && "rotate-180")} />
        </button>
      </div>
    </div>
  );
};

export default MapModeBar;
